import {
    DEFAULT_DATE_FORMAT,
    format,
    transformEnumToLabelValueOption,
} from '@fruitful/fruitils';
import { transformEnumToDisplay, UserType } from '@fruitful/member-profile';
import { CancellationReason } from '@fruitful/types';
import addYears from 'date-fns/addYears';
import differenceInDays from 'date-fns/differenceInDays';
import differenceInYears from 'date-fns/differenceInYears';
import formatDate from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { toZonedTime } from 'date-fns-tz';
import { useAtomValue } from 'jotai';
import { Badge } from 'primereact/badge';
import { Column, type ColumnEditorOptions } from 'primereact/column';
import {
    DataTable,
    type DataTableRowEditCompleteEvent,
} from 'primereact/datatable';
import { useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    ButtonVariant,
    CheckboxWithLabel,
    H6Emphasized,
    isEmpty,
    Spinner,
    type StackProps,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { ConfirmModal } from '../../common/components/ConfirmModal';
import {
    CheckboxEditor,
    DateTextEditor,
    DropdownEditor,
    TextAreaEditor,
    TextEditor,
} from '../../common/components/GridCellEditors';
import { MonospaceDetailText } from '../../common/components/GridCellRenderers';
import { Button } from '../../common/components/GroveButtons';
import { PageContentContainer } from '../../common/components/GroveContainers';
import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import {
    CANCELLATION_END_OF_DAY,
    CANCELLATION_TIME_ZONE,
} from '../../common/utils/cancellationDateUtils';
import {
    formatPhoneNumber,
    formatToFullPhoneNumber,
    formatToSimpleDateString,
    formatToSimpleDateTimeString,
} from '../../common/utils/formatting';
import {
    adminUserAtom,
    selectedMembershipAtom,
} from '../../entities/entityAtoms';
import {
    AdminUserRole,
    ExternalAccountLinkingPolicy,
    KYCVerificationStatus,
    type MemberDetails,
    OnboardingStatus,
    UserCategory,
} from '../../entities/entityTypes';
import { useResendPartnerInvitation } from '../../membership/hooks/useResendPartnerInvitation';
import { useResetReturningMemberAndRefetch } from '../../membership/hooks/useResetReturningMember';
import { useUpdateMemberAndRefetch } from '../hooks/useUpdateMember';
import { PronounsEditor } from './editors/PronounsEditor';
import { MemberJourneyLogSection } from './MemberJourneyLogSection';
import { CopyableTextField } from './renderers/CopyableTextField';
import { MemberAvatarRenderer } from './renderers/MemberAvatarRenderer';

interface MemberInfoProps extends StackProps {
    members: MemberDetails[];
}

export const MemberInfo = ({ members, ...stackProps }: MemberInfoProps) => {
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [isJourneyLogModalOpen, setIsJourneyLogModalOpen] = useState(false);
    const toast = useToastController();

    const { mutateAsync: resendPartnerInvitation } =
        useResendPartnerInvitation();
    const primaryMember = members.find(
        (m) => (m.type as UserType) === UserType.PRIMARY_MEMBER,
    );
    const partnerMember = members.find(
        (m) => (m.type as UserType) === UserType.PARTNER_MEMBER,
    );
    /* TODO: Identify a better onboarding indicator for partners. 
             As of now, date of birth is only collected during the partner join sequence,
             thus we rely on it to indicate whether the partner has 'accepted' their invite or not.  */
    const isResendAvailable = partnerMember?.dateOfBirth === null;

    const resendPartnerInvite = async () => {
        try {
            if (primaryMember && partnerMember) {
                await resendPartnerInvitation({
                    urlParams: { id: primaryMember.id },
                    body: {
                        partnerEmail: partnerMember.email,
                        partnerStytchId: partnerMember.stytchId,
                    },
                });

                toast.show('Invite sent!', MutationSuccessToastOptions);
            } else {
                toast.show(
                    'Unable to find primary or partner member',
                    MutationErrorToastOptions(),
                );
            }
        } catch (err) {
            toast.show('Error sending invitation', MutationErrorToastOptions());
        }
    };

    return (
        <PageContentContainer {...stackProps}>
            <YStack gap={16} width="100%">
                <XStack justifyContent="space-between" width="100%">
                    <H6Emphasized>Member info</H6Emphasized>

                    <XStack gap="$2">
                        {/* Group buttons together */}
                        {isResendAvailable && (
                            <Button
                                variant="secondary"
                                size="small"
                                onPress={resendPartnerInvite}
                            >
                                Resend Partner Invitation
                            </Button>
                        )}
                        <Button
                            variant="secondary"
                            size="small"
                            onPress={() => {
                                setIsJourneyLogModalOpen(true);
                            }}
                        >
                            Member journey log
                        </Button>
                        <Button
                            variant="secondary"
                            size="small"
                            onPress={() => {
                                setIsDetailsModalOpen(true);
                            }}
                        >
                            View details
                        </Button>
                    </XStack>
                </XStack>
                <MemberInfoTable members={members} />
            </YStack>
            <GroveSimpleModal
                isOpen={isJourneyLogModalOpen}
                onClose={() => {
                    setIsJourneyLogModalOpen(false);
                }}
                renderInAppRoot={false}
            >
                <YStack
                    maxWidth="min(96vw, 1400px)"
                    width="100%"
                    padding={24}
                    paddingBottom={48}
                    gap={16}
                >
                    <MemberJourneyLogSection members={members} />
                </YStack>
            </GroveSimpleModal>
            <GroveSimpleModal
                isOpen={isDetailsModalOpen}
                onClose={() => {
                    setIsDetailsModalOpen(false);
                }}
                renderInAppRoot={false} // When this is true, the PrimeReact components are stacked below the modal.
            >
                <MemberDetailsTable members={members} />
            </GroveSimpleModal>
        </PageContentContainer>
    );
};

const MemberInfoTable = ({ members }: { members: MemberDetails[] }) => {
    return members == null || members.length === 0 ? null : (
        <DataTable value={members} size="small" tableStyle={{ width: '100%' }}>
            <Column
                field="id"
                header="Profile"
                body={MemberAvatarRenderer}
                style={{ width: '80px' }}
            />
            <Column field="id" header="Name" body={MemberIdInfoRenderer} />
            <Column
                field="email"
                header="Contact"
                body={MemberContactInfoRenderer}
            />
            <Column field="dateOfBirth" header="Age" body={MemberAgeRenderer} />
        </DataTable>
    );
};

const MemberDetailsTable = ({ members }: { members: MemberDetails[] }) => {
    const toast = useToastController();

    const adminUser = useAtomValue(adminUserAtom);
    const canEditMember =
        adminUser.authorization.selectedMembership.general.canEdit;
    const canEditMemberPii =
        adminUser.authorization.selectedMembership.pii.canEdit;
    const isGuide =
        adminUser.role === AdminUserRole.GUIDE ||
        adminUser.role === AdminUserRole.GUIDE_LEAD;

    const membership = useAtomValue(selectedMembershipAtom);

    const [showAllFields, setShowAllFields] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const shouldHideFieldForGuide = isGuide && !showAllFields;

    const { updateMemberAsync } = useUpdateMemberAndRefetch(membership);
    const { resetReturningMemberAsync } =
        useResetReturningMemberAndRefetch(membership);

    const onRowEditComplete = async ({
        newData,
    }: DataTableRowEditCompleteEvent) => {
        setIsSubmitting(true);
        const member: MemberDetails = newData as MemberDetails;

        const existingMember = members.find((m) => m.id === member.id);

        try {
            // By default newData is the full row of data, and the updateMember call will make requests to third-party services.
            // So, we limit calling these third-parties by omitting props on the updated data that have not changed that are used in third-party service calls.
            const upsertMemberBody = {
                ...(member.firstName != null &&
                member.firstName !== existingMember?.firstName
                    ? { firstName: member.firstName }
                    : {}),
                ...(member.lastName != null &&
                member.lastName !== existingMember?.lastName
                    ? { lastName: member.lastName }
                    : {}),
                ...(member.email != null &&
                member.email !== existingMember?.email
                    ? { email: member.email }
                    : {}),
                ...(member.phoneNumber != null &&
                member.phoneNumber !== existingMember?.phoneNumber
                    ? {
                          phoneNumber: formatToFullPhoneNumber(
                              member.phoneNumber,
                          ),
                      }
                    : {}),
                ...(member.dateOfBirth != null &&
                member.dateOfBirth !== existingMember?.dateOfBirth
                    ? {
                          dateOfBirth: member.dateOfBirth,
                      }
                    : {}),
                pronouns: member.pronouns,
                onboardingStatus: member.onboardingStatus,
                externalAccountLinkingPolicy:
                    member.externalAccountLinkingPolicy,
                isSubscriptionActive: member.isSubscriptionActive,
                disableMfa: member.disableMfa,
                spinwheelOverride: member.spinwheelOverride,
                cancellationDate: member.cancellationDate,
                cancellationReason: member.cancellationReason,
                cancellationNotes: member.cancellationNotes,
                category: member.category,
            };
            await updateMemberAsync(member.id, upsertMemberBody);

            toast.show('Member updated!', MutationSuccessToastOptions);
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            toast.show(
                'Error updating member',
                MutationErrorToastOptions(errorMessage),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const onResetReturningMember = async (userId: string) => {
        if (membership?.isMembershipActive) {
            toast.show(
                'Membership is ACTIVE, so the "Reset returning member" action is invalid',
                MutationErrorToastOptions(),
            );
            return;
        }
        if (!canEditMemberPii) {
            toast.show(
                'You do not have permission to reset returning member',
                MutationErrorToastOptions(),
            );
            return;
        }

        try {
            setIsSubmitting(true);
            await resetReturningMemberAsync(userId);
        } catch (error) {
            toast.show(
                'Error resetting returning member',
                MutationErrorToastOptions(),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <YStack maxWidth="90vw" padding={24} paddingBottom={48} gap={32}>
            <H6Emphasized>Member details</H6Emphasized>
            {isSubmitting ? (
                <Spinner />
            ) : (
                <>
                    {/* For now, only Guides have views that hide certain fields, so expose this override to them. */}
                    {isGuide && (
                        <CheckboxWithLabel
                            id="show-all-member-detail-fields"
                            label="Show all fields?"
                            checked={showAllFields}
                            onPress={() => {
                                setShowAllFields((prev) => !prev);
                            }}
                        />
                    )}
                    <DataTable
                        value={members}
                        size="small"
                        tableStyle={{ width: '100%', fontSize: 13 }}
                        editMode="row"
                        onRowEditComplete={onRowEditComplete}
                        scrollable
                    >
                        <Column
                            field="id"
                            header="id"
                            hidden={shouldHideFieldForGuide}
                            body={({ id }: MemberDetails) => {
                                return (
                                    <CopyableTextField value={id}>
                                        {id}
                                    </CopyableTextField>
                                );
                            }}
                        />
                        <Column
                            field="firstName"
                            header="First Name"
                            editor={
                                canEditMemberPii
                                    ? (options) => TextEditor({ options })
                                    : null
                            }
                            style={{
                                minWidth: 150,
                            }}
                        />
                        <Column
                            field="lastName"
                            header="Last Name"
                            editor={
                                canEditMemberPii
                                    ? (options) => TextEditor({ options })
                                    : null
                            }
                            style={{
                                minWidth: 150,
                            }}
                        />
                        <Column
                            field="email"
                            header="Email"
                            editor={
                                canEditMemberPii
                                    ? (options) => TextEditor({ options })
                                    : null
                            }
                            style={{
                                minWidth: 150,
                            }}
                            body={({ email }: MemberDetails) => {
                                return (
                                    <CopyableTextField value={email}>
                                        {email}
                                    </CopyableTextField>
                                );
                            }}
                        />
                        <Column
                            field="category"
                            header="Category"
                            body={({ category }: MemberDetails) => {
                                return transformEnumToDisplay(category);
                            }}
                            editor={
                                canEditMemberPii
                                    ? (options) => UserCategoryEditor(options)
                                    : null
                            }
                        />
                        <Column
                            field="phoneNumber"
                            header="Phone Number"
                            body={({ phoneNumber }: MemberDetails) => {
                                return (
                                    <CopyableTextField
                                        value={
                                            phoneNumber?.replace('+1', '') ?? ''
                                        }
                                    >
                                        {formatPhoneNumber(phoneNumber)}
                                    </CopyableTextField>
                                );
                            }}
                            editor={
                                canEditMemberPii
                                    ? (options) => TextEditor({ options })
                                    : null
                            }
                            style={{
                                minWidth: 150,
                            }}
                        />
                        <Column
                            field="dateOfBirth"
                            header="Date of Birth"
                            body={({ dateOfBirth }: MemberDetails) => {
                                return formatToSimpleDateString(dateOfBirth, {
                                    timeZone: 'UTC',
                                });
                            }}
                            editor={(options) => DateTextEditor({ options })}
                            style={{
                                minWidth: 150,
                            }}
                        />
                        <Column
                            field="pronouns"
                            header="Pronouns"
                            editor={(options) => PronounsEditor(options)}
                        />
                        <Column
                            field="createdAt"
                            header="Created At ⓘ"
                            headerTooltip="When the user account was created (during signup)"
                            body={({ createdAt }: MemberDetails) => {
                                return formatToSimpleDateTimeString(createdAt);
                            }}
                        />
                        <Column
                            field="updatedAt"
                            header="Updated At"
                            hidden={shouldHideFieldForGuide}
                            body={({ updatedAt }: MemberDetails) => {
                                return formatToSimpleDateTimeString(updatedAt);
                            }}
                        />
                        <Column
                            field="type"
                            header="Member Type"
                            body={({ type }: MemberDetails) => {
                                return transformEnumToDisplay(type);
                            }}
                        />
                        <Column
                            field="isSubscriptionActive"
                            header="Is Subscription Active? ⓘ"
                            headerTooltip="isSubscriptionActive - Whether this user is part of an active membership (as primary member, partner member, or joint account holder)"
                            hidden={shouldHideFieldForGuide}
                            editor={
                                canEditMemberPii
                                    ? (options) => CheckboxEditor({ options })
                                    : null
                            }
                        />
                        <Column
                            field="onboardingStatus"
                            header="Onboarding status ⓘ"
                            body={MemberOnboardingStatusRenderer}
                            headerTooltip={`If not ${OnboardingStatus.COMPLETED} or ${OnboardingStatus.JOINT_COMPLETED}, the account/membership onboarding is not complete`}
                            editor={
                                canEditMemberPii
                                    ? (options) =>
                                          OnboardingStatusEditor(options)
                                    : null
                            }
                        />
                        <Column
                            field="disableMfa"
                            header="Disable MFA?"
                            hidden={shouldHideFieldForGuide}
                            editor={
                                canEditMemberPii
                                    ? (options) => CheckboxEditor({ options })
                                    : null
                            }
                        />
                        <Column
                            field="spinwheelOverride"
                            header="Spinwheel Override?"
                            hidden={shouldHideFieldForGuide}
                            editor={
                                canEditMemberPii
                                    ? (options) => CheckboxEditor({ options })
                                    : null
                            }
                        />
                        <Column
                            field="syncteraKycStatus"
                            header="syncteraKycStatus"
                            body={MemberKycStatusRenderer}
                        />
                        <Column
                            field="depositoryAccountLimit"
                            header="Depository Account Limit"
                            hidden={shouldHideFieldForGuide}
                        />
                        <Column
                            field="externalAccountLinkingPolicy"
                            header="External Account Linking Policy"
                            body={({
                                externalAccountLinkingPolicy,
                            }: MemberDetails) => {
                                return transformEnumToDisplay(
                                    externalAccountLinkingPolicy,
                                );
                            }}
                            editor={
                                canEditMemberPii
                                    ? (options) =>
                                          ExternalAccountLinkingPolicyEditor(
                                              options,
                                          )
                                    : null
                            }
                        />
                        <Column
                            field="stytchId"
                            header="Stytch Id"
                            hidden={shouldHideFieldForGuide}
                        />
                        <Column
                            field="stripeCustomerId"
                            header="Stripe Customer Id"
                            hidden={shouldHideFieldForGuide}
                        />
                        <Column
                            field="sendbirdUserId"
                            header="Sendbird User Id"
                            hidden={shouldHideFieldForGuide}
                        />
                        <Column
                            field="syncteraPersonId"
                            header="Synctera Person Id"
                            hidden={shouldHideFieldForGuide}
                        />
                        <Column
                            field="quilttProfileId"
                            header="Quiltt Profile Id"
                            hidden={shouldHideFieldForGuide}
                        />
                        <Column
                            field="cancellationDate"
                            header="Cancellation Date"
                            body={({ cancellationDate }: MemberDetails) => {
                                if (
                                    cancellationDate === null ||
                                    cancellationDate === undefined ||
                                    cancellationDate === ''
                                ) {
                                    return undefined;
                                }
                                // always show the cancellation date in Pacific Time
                                // because we store cancellation dates as 11:59:59 PM Pacific Time,
                                // and we want to show the "date" of the cancellation here,
                                // grabbing the date of the timestamp in Pacific Time gets us the correct date.
                                return format(
                                    toZonedTime(
                                        cancellationDate,
                                        CANCELLATION_TIME_ZONE,
                                    ),
                                    DEFAULT_DATE_FORMAT,
                                );
                            }}
                            editor={
                                canEditMemberPii
                                    ? (options) =>
                                          DateTextEditor({
                                              options,
                                              timeZone: CANCELLATION_TIME_ZONE,
                                              timeOfDay:
                                                  CANCELLATION_END_OF_DAY,
                                          })
                                    : null
                            }
                            style={{
                                minWidth: 150,
                            }}
                        />
                        <Column
                            field="cancellationReason"
                            header="Cancellation Reason ⓘ"
                            headerTooltip="Our description of why the membership was canceled, provided when we end the membership"
                            headerTooltipOptions={{ position: 'top' }}
                            body={({ cancellationReason }: MemberDetails) => {
                                return cancellationReason != null
                                    ? transformEnumToDisplay(cancellationReason)
                                    : 'N/A';
                            }}
                            editor={
                                canEditMemberPii
                                    ? (options) =>
                                          CancellationReasonEditor(options)
                                    : null
                            }
                            style={{
                                minWidth: 150,
                            }}
                        />
                        <Column
                            field="cancellationNotes"
                            header="Cancellation Notes"
                            headerTooltipOptions={{ position: 'top' }}
                            editor={
                                canEditMemberPii
                                    ? (options) => TextAreaEditor({ options })
                                    : null
                            }
                            style={{
                                minWidth: 150,
                            }}
                        />
                        <Column rowEditor hidden={!canEditMember} />
                        <Column
                            hidden={
                                membership?.isMembershipActive !== false ||
                                shouldHideFieldForGuide ||
                                !canEditMemberPii
                            }
                            header="Deactivate User for returning member ⓘ"
                            headerTooltip="Deactivate inactive user data so they can create a new Fruitful account"
                            body={(details: MemberDetails) => {
                                const [showConfirm, setShowConfirm] =
                                    useState(false);
                                return (
                                    <>
                                        <Button
                                            variant={ButtonVariant.SECONDARY}
                                            tone="danger"
                                            size="small"
                                            onPress={() => {
                                                setShowConfirm(true);
                                            }}
                                        >
                                            Deactivate User
                                        </Button>
                                        <ConfirmModal
                                            isOpen={showConfirm}
                                            onClose={() => {
                                                setShowConfirm(false);
                                            }}
                                            onConfirm={async () => {
                                                setShowConfirm(false);
                                                await onResetReturningMember(
                                                    details.id,
                                                );
                                            }}
                                            description="This will delete this member from Stytch and change their email address so this member can create a new Fruitful account.  Sound good?"
                                            noText="Hold on..."
                                        />
                                    </>
                                );
                            }}
                        />
                    </DataTable>
                </>
            )}
        </YStack>
    );
};

const MemberIdInfoRenderer = ({ id, firstName, lastName }: MemberDetails) => {
    return (
        <YStack>
            <CopyableTextField value={`${firstName} ${lastName}`}>
                <BodyLargeEmphasized>{`${firstName} ${lastName}`}</BodyLargeEmphasized>
            </CopyableTextField>
            <CopyableTextField value={id}>
                <MonospaceDetailText>{id}</MonospaceDetailText>
            </CopyableTextField>
        </YStack>
    );
};

const MemberContactInfoRenderer = ({ email, phoneNumber }: MemberDetails) => {
    return (
        <YStack>
            <CopyableTextField value={email}>
                <BodyMediumRegular>{email}</BodyMediumRegular>
            </CopyableTextField>
            <CopyableTextField value={phoneNumber?.replace('+1', '') ?? ''}>
                <MonospaceDetailText>
                    {formatPhoneNumber(phoneNumber)}
                </MonospaceDetailText>
            </CopyableTextField>
        </YStack>
    );
};

const MemberAgeRenderer = ({ dateOfBirth }: MemberDetails) => {
    if (isEmpty(dateOfBirth) || dateOfBirth == null) {
        return null;
    }

    const birthDateWithoutTimeZone = dateOfBirth.split('T')[0] ?? dateOfBirth;
    const birthDate = parseISO(birthDateWithoutTimeZone);
    const today = new Date();
    const todayAtMidnight = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
    );
    const age = differenceInYears(today, birthDate);
    const thisYearBirthday = new Date(
        today.getFullYear(),
        birthDate.getMonth(),
        birthDate.getDate(),
    );
    const nextBirthday =
        thisYearBirthday >= todayAtMidnight
            ? thisYearBirthday
            : addYears(thisYearBirthday, 1);
    const isBirthdaySoon =
        differenceInDays(nextBirthday, todayAtMidnight) <= 30;

    return (
        <YStack>
            <BodyMediumRegular>{age}</BodyMediumRegular>
            {isBirthdaySoon && (
                <Badge value={`🎂  ${formatDate(nextBirthday, 'EEE MMM d')}`} />
            )}
        </YStack>
    );
};

const MemberOnboardingStatusRenderer = ({
    onboardingStatus,
}: MemberDetails) => {
    return isEmpty(onboardingStatus) ? null : (
        <Badge
            severity={
                [
                    OnboardingStatus.COMPLETED,
                    OnboardingStatus.JOINT_COMPLETED,
                ].includes(onboardingStatus)
                    ? 'info'
                    : 'warning'
            }
            value={onboardingStatus}
        />
    );
};

const MemberKycStatusRenderer = ({ syncteraKycStatus }: MemberDetails) => {
    if (isEmpty(syncteraKycStatus) || syncteraKycStatus == null) {
        return null;
    }

    const isSuccess = [
        KYCVerificationStatus.ACCEPTED,
        KYCVerificationStatus.PROVISIONAL,
    ].includes(syncteraKycStatus);
    const isError =
        !isSuccess &&
        [
            KYCVerificationStatus.REJECTED,
            KYCVerificationStatus.VENDOR_ERROR,
        ].includes(syncteraKycStatus);

    return (
        <Badge
            severity={isSuccess ? 'success' : isError ? 'danger' : 'warning'}
            value={syncteraKycStatus}
        />
    );
};

const OnboardingStatusEditor = (options: ColumnEditorOptions) => {
    const onboardingStatuses = Object.values(OnboardingStatus).map(
        (onboardingStatus) => ({
            label: transformEnumToDisplay(onboardingStatus),
            value: onboardingStatus,
        }),
    );

    return (
        <DropdownEditor
            options={options}
            dropdownProps={{
                options: onboardingStatuses,
                placeholder: 'Select Onboarding Status',
            }}
        />
    );
};

const ExternalAccountLinkingPolicyEditor = (options: ColumnEditorOptions) => {
    const extAccLinkPolicies = Object.values(ExternalAccountLinkingPolicy).map(
        (extAccLinkPolicy) => ({
            label: transformEnumToDisplay(extAccLinkPolicy),
            value: extAccLinkPolicy,
        }),
    );

    return (
        <DropdownEditor
            options={options}
            dropdownProps={{
                options: extAccLinkPolicies,
                placeholder: 'Select External Account Linking Policy',
            }}
        />
    );
};

const UserCategoryEditor = (editorOptions: ColumnEditorOptions) => {
    const dropdownOptions = Object.values(UserCategory).map((category) => ({
        label: transformEnumToDisplay(category),
        value: category,
    }));

    return (
        <DropdownEditor
            options={editorOptions}
            dropdownProps={{
                options: dropdownOptions,
                placeholder: 'Select Category',
            }}
        />
    );
};

const CancellationReasonEditor = (options: ColumnEditorOptions) => {
    return (
        <DropdownEditor
            options={options}
            dropdownProps={{
                options: transformEnumToLabelValueOption(CancellationReason),
                placeholder: 'Select Cancellation Reason',
            }}
        />
    );
};
